import { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { TABBAR_ITEM_TRANSITION } from '../../../../constants/ui/tabbar';
import { GRHuntingAllStarsIconActiveSvg, GRHuntingAllStarsIconSvg, GRTabbarIconSvg } from '../../assets/svg';
import { GRSkinId, GRSkinIds } from '../../types/global';

const bounceAnimation = keyframes`
  47% {
    transform: scale(1);
    filter: brightness(100%);
  }

  50% {
    transform: scale(1.2);
    filter: brightness(200%);
  }

  53% {
    transform: scale(1);
    filter: brightness(100%);
  }
`;

type WrapperProps = {
  $animate: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  width: var(--GRNavIcon-width, 2em);
  height: var(--GRNavIcon-height, 2em);
  margin: 0;
  padding: 0;
  text-align: center;

  position: relative;
  overflow: hidden;

  transition: filter ${TABBAR_ITEM_TRANSITION}ms ease-in-out;

  ${({ $animate }) => css`
    ${$animate
      ? css`
          animation: ${bounceAnimation} 3000ms ease-in-out infinite;
        `
      : undefined}
  `}
`;

type StyledGRIconProps = {
  $isHighlight: boolean;
};

const sharedIconStyle = css<StyledGRIconProps>`
  width: 100%;
  height: 100%;
`;

const StyledGRIcon = styled(GRTabbarIconSvg)`
  ${sharedIconStyle};
  ${({ $isHighlight }) => css`
    & .gr-tabbar-icon-hat {
      transform-origin: left center;
      transition: transform 500ms ease-in-out;

      ${$isHighlight
        ? css`
            transform: rotateZ(-20deg) translate(-10%, 5%);
          `
        : undefined}
    }

    & .gr-tabbar-icon-sparkles {
      filter: drop-shadow(0em 0em 1em white);
    }

    & .gr-tabbar-icon-sparkle {
      transition: stroke-dashoffset 200ms ease-in-out;
      stroke-dasharray: 3;
      stroke-dashoffset: 3;

      ${$isHighlight
        ? css`
            stroke-dashoffset: 0;
          `
        : undefined};
    }
  `}
`;

const StyledGRHuntingAllStarsIcon = styled(GRHuntingAllStarsIconSvg)`
  ${sharedIconStyle};
  transform: translateY(0.1em);

  &&& {
    height: 85%;
  }
`;

const StyledGRHuntingAllStarsIconActive = styled(GRHuntingAllStarsIconActiveSvg)`
  ${sharedIconStyle};
  transform: translateY(0.1em);

  &&& {
    height: 85%;
  }
`;

type GRTabIconProps = {
  className?: string;
  skinId?: GRSkinId;
  isHighlight?: boolean;
  disableAnimation?: boolean;
};

const GRTabIcon = ({
  className = '',
  skinId = GRSkinIds.GIFT_RACE,
  isHighlight = false,
  disableAnimation = false,
}: GRTabIconProps) => {
  const MenuIcon = useMemo(() => {
    switch (skinId) {
      case GRSkinIds.HUNTING_ALL_STARS:
        return StyledGRHuntingAllStarsIcon;
      case GRSkinIds.GIFT_RACE:
      default:
        return StyledGRIcon;
    }
  }, [skinId]);

  const MenuIconActive = useMemo(() => {
    switch (skinId) {
      case GRSkinIds.HUNTING_ALL_STARS:
        return StyledGRHuntingAllStarsIconActive;
      case GRSkinIds.GIFT_RACE:
      default:
        return StyledGRIcon;
    }
  }, [skinId]);
  return (
    <Wrapper className={className} $animate={!disableAnimation}>
      {isHighlight ? <MenuIconActive $isHighlight={isHighlight} /> : <MenuIcon $isHighlight={isHighlight} />}
    </Wrapper>
  );
};

export default GRTabIcon;
